import { DialogComponent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';
import { syncAssetsFromDocument } from '@theme/async-assets';
import { CartAddEvent } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { morph } from '@theme/morph';

/**
 * Bottom-Up Sheet — modale Hülle, slidet von unten herein.
 *
 * Content-Modi:
 *  - Inline-Children im Snippet (kein src)
 *  - Lazy-Fetch via Section Rendering API (src mit ?section_id=…)
 *  - Lazy-Fetch volle Seite (src ohne section_id, optional + selector)
 *
 * Mobile-UX:
 *  - Swipe-down am Handle/Header → close (koexistent mit Body-Scroll)
 *  - Browser-Back → close, propagiert nicht zur Page-Navigation
 *  - prefers-reduced-motion → animationless (via CSS)
 *
 * Wichtig: `showDialog`/`closeDialog` sind in DialogComponent class-field
 * Arrow-Functions (Instance-Properties), nicht Prototype-Methods.
 * `super.closeDialog()` würde nicht funktionieren. Stattdessen reagieren wir
 * auf die dispatched `dialog:open` / `dialog:close` Events.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} body
 * @property {HTMLButtonElement} [handle]
 *
 * @extends {DialogComponent}
 */
class BottomUpSheetComponent extends DialogComponent {
  requiredRefs = ['dialog', 'body'];

  /** @type {Promise<void> | null} */
  #loadPromise = null;

  /** @type {number} — sequence token: a stale fetch must not overwrite a newer one */
  #fetchToken = 0;

  /** @type {string | null} — location (path+search) the lazy section content was loaded for */
  #loadedForUrl = null;

  /**
   * Lazy-section mode: the sheet content is a scoped fragment of a section
   * (render-eager-flag `_async=<scope>`), fetched against the CURRENT location
   * at open time. Used by the collection filter sheet — its content depends on
   * the live filter URL, unlike the static `src` mode.
   */
  get #isLazySection() {
    return Boolean(this.dataset.sectionId && this.dataset.asyncScope);
  }

  /** @type {boolean} — wurde ein synthetischer History-Eintrag gepusht? */
  #historyPushed = false;

  /** @type {boolean} — wird Close gerade durch popstate ausgelöst? (skip history.back) */
  #closingFromPopState = false;

  /** @type {ReturnType<typeof setTimeout> | null} — Sicherheits-Timeout für den Filter-Refresh-Indikator */
  #filteringTimeout = null;

  /** @type {ScrollRestoration} — gemerkter history.scrollRestoration-Modus vor dem Öffnen */
  #prevScrollRestoration = 'auto';

  /** @type {number} — Scroll-Offset beim Öffnen (aus body.style.top), für Re-Assert nach Close */
  #openScrollY = 0;

  /** @type {Array<{ container: HTMLElement, headline: string }>} — pushed views (drilldown back-stack) */
  #viewStack = [];

  /** @type {boolean} — body holds local (openWithContent) content → #onDialogOpen must not fetch,
   * and the next openFor must refresh even if the URL is unchanged. Does NOT touch src/selector,
   * so the sheet's static config (e.g. quickview selector) survives a local-content interlude. */
  #localContent = false;

  // ===== Touch-Drag-State =====
  /** @type {number | null} */
  #dragStartY = null;
  /** @type {'handle' | 'header' | 'body-top' | null} */
  #dragOrigin = null;
  /** @type {number} */
  #dragDelta = 0;
  /** @type {number} */
  #dragStartTime = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('preload')) {
      if (this.hasAttribute('src') || this.#isLazySection) this.#ensureLoaded();
    }

    this.addEventListener('dialog:open', this.#onDialogOpen);
    this.addEventListener('dialog:close', this.#onDialogClose);
    // Add-to-cart from within the sheet (e.g. the PDP quickview) → close the sheet.
    // CartAddEvent bubbles, so listening on the sheet only catches adds that
    // originated inside it (not quantity changes elsewhere).
    this.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    // Filter-/Sort-Änderungen innerhalb des Sheets stoßen einen Section-Fetch+Morph an
    // (filter:update bubbelt von facet-inputs-component). Solange das läuft → Indikator.
    this.addEventListener('filter:update', this.#onFilterUpdate);
    // In-sheet navigation: links inside an opted-in container reload the sheet
    // instead of navigating the whole page (see #handleBodyClick).
    this.refs.body.addEventListener('click', this.#handleBodyClick);
    window.addEventListener('popstate', this.#handlePopState);

    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#onDialogOpen);
    this.removeEventListener('dialog:close', this.#onDialogClose);
    this.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    this.removeEventListener('filter:update', this.#onFilterUpdate);
    this.refs.body.removeEventListener('click', this.#handleBodyClick);
    if (this.#filteringTimeout) clearTimeout(this.#filteringTimeout);
    window.removeEventListener('popstate', this.#handlePopState);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('touchcancel', this.#handleTouchEnd);
  }

  /**
   * Add-to-cart inside the sheet → close it. Guard on the CartAddEvent type so
   * other bubbling cart:update events (e.g. quantity changes) don't close it.
   * @param {Event} event
   */
  #onCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (this.refs.dialog?.open) this.closeDialog();
  };

  /**
   * Trigger-Zeit Open: setzt src (und optional selector) und öffnet.
   * Verwendung: <button on:click="#sheet-id/openFor?url=/products/foo">
   *
   * @param {{ url?: string, selector?: string, headline?: string }} data
   */
  openFor = (data = {}) => {
    const urlChanged = data.url && data.url !== this.getAttribute('src');
    // A previous openWithContent left local content in the body — even if the URL is
    // unchanged we must re-fetch, otherwise the stale local content would show.
    const wasLocal = this.#localContent;
    this.#localContent = false;
    if (data.url) this.setAttribute('src', data.url);
    if (data.selector) this.setAttribute('selector', data.selector);
    if (data.headline) {
      const headline = this.querySelector('.bottom-up-sheet__headline');
      if (headline) headline.textContent = data.headline;
    }
    if (urlChanged || wasLocal) {
      this.refs.body.replaceChildren();
      this.refresh();
    }
    this.showDialog();
  };

  /**
   * Cache invalidieren und ggf. neu fetchen (wenn Sheet offen).
   */
  refresh = () => {
    this.#loadPromise = null;
    this.classList.add('bottom-up-sheet--loading');
    this.refs.body.setAttribute('aria-busy', 'true');
    if (this.refs.dialog.open) this.#ensureLoaded();
  };

  // ===== View-Stack (Drilldown push/pop) =====

  /**
   * Open the sheet showing LOCAL content as the first view (no URL fetch).
   * Used by out-of-sheet [data-sheet-push] triggers.
   * @param {{ content?: Node, headline?: string }} data
   */
  openWithContent = ({ content, headline = '' } = {}) => {
    if (!content) return;
    // Local content → flag so #onDialogOpen skips the lazy fetch. We must NOT remove
    // src/selector here: the quickview sheet carries a static selector that the next
    // product-card openFor relies on (it passes only url).
    this.#localContent = true;
    this.#clearViewStack();
    this.refs.body.replaceChildren(content);
    this.refs.body.scrollTop = 0;
    this.#setHeadline(headline);
    this.showDialog();
  };

  /**
   * Push a new local view; the current view's live nodes are detached onto the
   * stack and restored verbatim on popView (the close-button becomes a back-button).
   * @param {{ content?: Node, headline?: string }} data
   */
  pushView = ({ content, headline = '' } = {}) => {
    if (!content) return;
    // Pin the current body height so the sheet does NOT collapse to the (often
    // shorter) pushed view — keeps the dialog height stable when drilling down
    // from a tall quickview PDP. Cleared on pop-to-root / close.
    const currentHeight = this.refs.body.getBoundingClientRect().height;
    const holder = document.createElement('div');
    holder.append(...this.refs.body.childNodes);
    this.#viewStack.push({ container: holder, headline: this.#getHeadline() });
    this.refs.body.replaceChildren(content);
    if (currentHeight > 0) this.refs.body.style.minHeight = `${currentHeight}px`;
    this.refs.body.scrollTop = 0;
    this.#setHeadline(headline);
    this.#syncNavButton();
    this.#animateView('push');
  };

  /** Pop the top view, restoring the previous one. */
  popView = () => {
    const prev = this.#viewStack.pop();
    if (!prev) return;
    this.refs.body.replaceChildren(...prev.container.childNodes);
    // Back at the root view → release the pinned height so it can size naturally again.
    if (this.#viewStack.length === 0) this.refs.body.style.minHeight = '';
    this.refs.body.scrollTop = 0;
    this.#setHeadline(prev.headline);
    this.#syncNavButton();
    this.#animateView('pop');
  };

  /** Header button: back if a view is stacked, otherwise close. Bound via on:click="/closeOrBack". */
  closeOrBack = () => {
    if (this.#viewStack.length) this.popView();
    else this.closeDialog();
  };

  /** @param {Element} trigger */
  #pushFromTrigger(trigger) {
    const content = resolveSheetPushContent(trigger);
    if (content) this.pushView({ content, headline: trigger.getAttribute('data-headline') ?? '' });
  }

  #clearViewStack() {
    this.#viewStack = [];
    this.classList.remove('bottom-up-sheet--nested');
    this.refs.body.style.minHeight = '';
  }

  #syncNavButton() {
    this.classList.toggle('bottom-up-sheet--nested', this.#viewStack.length > 0);
  }

  /**
   * Animate the incoming view on push/pop so the swap matches the otherwise-animated
   * sheet: push slides in from the right, pop from the left (+ fade). CSS-gated behind
   * prefers-reduced-motion. Re-add after reflow so the animation restarts each time.
   * @param {'push' | 'pop'} direction
   */
  #animateView(direction) {
    const body = this.refs.body;
    body.classList.remove('bottom-up-sheet__body--push', 'bottom-up-sheet__body--pop');
    void body.offsetWidth;
    body.classList.add(direction === 'pop' ? 'bottom-up-sheet__body--pop' : 'bottom-up-sheet__body--push');
  }

  #getHeadline() {
    return this.querySelector('.bottom-up-sheet__headline')?.textContent ?? '';
  }

  /** @param {string} text */
  #setHeadline(text) {
    const el = this.querySelector('.bottom-up-sheet__headline');
    if (el) el.textContent = text;
  }

  // ===== Lifecycle (via Events statt Override) =====

  #onDialogOpen = () => {
    // Synthetischer History-Eintrag — Browser-Back schließt nur das Sheet,
    // navigiert nicht zur vorigen Page.
    // Übersprungen bei data-no-history: wenn der Sheet-Inhalt die URL selbst
    // manipuliert (z.B. Collection-Filter), würde history.back() beim Close
    // die gefilterten Ergebnisse verlassen statt nur zu schließen.
    if (!this.hasAttribute('data-no-history') && !this.#historyPushed) {
      // Scroll-Restore unterdrücken: Beim Öffnen ist der Body bereits position:fixed
      // (scrollY=0). 'manual' MUSS am aktuellen Eintrag (vor pushState) gesetzt sein,
      // denn history.back() beim Schließen navigiert genau hierher zurück — sonst
      // auto-restauriert der Browser auf den geclobberten Offset 0 → Sprung nach oben.
      if ('scrollRestoration' in history) {
        this.#prevScrollRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      }
      // Scroll-Offset für Re-Assert sichern (Body ist fixed mit top:-Y).
      this.#openScrollY = -1 * parseInt(document.body.style.top || '0', 10) || 0;
      history.pushState({ bottomUpSheet: this.id }, '', '');
      this.#historyPushed = true;
    }

    // Skip all fetching when the body holds local (openWithContent) content — but the
    // static src/selector stay intact for the next fetch-mode open.
    if (this.#localContent) return;

    if (this.#isLazySection) {
      const currentUrl = location.pathname + location.search;
      const selector = this.getAttribute('selector');
      if (!this.#loadedForUrl && selector && this.refs.body.querySelector(selector)) {
        // Server-rendered eager content (theme editor) — adopt, don't fetch.
        this.#loadedForUrl = currentUrl;
      } else if (this.#loadedForUrl && this.#loadedForUrl !== currentUrl) {
        // Filters changed while the sheet was closed (pills, featured tiles,
        // back/forward) — the morph skipped our body, so it is stale.
        this.refreshLazySection();
      } else {
        this.#ensureLoaded();
      }
    } else if (this.hasAttribute('src')) {
      this.#ensureLoaded();
    }
  };

  #onDialogClose = () => {
    // Inline-Transform (vom Drag) zurücksetzen.
    this.refs.dialog.style.transform = '';
    this.refs.dialog.style.transition = '';
    this.#resetDragState();

    // History-Eintrag konsumieren + Scroll-Position absichern.
    if (this.#historyPushed) {
      const targetY = this.#openScrollY;
      const prevRestoration = this.#prevScrollRestoration;
      // scrollRestoration erst NACH dem Back-Scroll-Step zurücksetzen + Scroll
      // re-asserten (Belt-and-Suspenders gegen Browser-Eigenheiten).
      const afterBack = () => {
        if ('scrollRestoration' in history) history.scrollRestoration = prevRestoration;
        if (targetY) window.scrollTo({ top: targetY, behavior: 'instant' });
      };

      if (!this.#closingFromPopState) {
        // Selbst ausgelöster Close → history.back(); Aufräumen nach dessen popstate.
        const onBackPop = () => {
          window.removeEventListener('popstate', onBackPop);
          requestAnimationFrame(afterBack);
        };
        window.addEventListener('popstate', onBackPop);
        history.back();
      } else {
        // Close kam vom Browser-Back (popstate bereits erfolgt).
        requestAnimationFrame(afterBack);
      }
    }
    this.#historyPushed = false;
    this.#closingFromPopState = false;
    this.#clearViewStack();
    this.#stopFiltering();
  };

  /**
   * Filter/Sort wurde im Sheet geändert → Section wird nachgeladen+gemorpht.
   * Indikator anzeigen. Lazy-Section-Sheets refreshen ihren Body selbst (der
   * Section-Morph überspringt ihn per data-skip-*) — der Refresh-Fetch startet
   * VOR dem Morph-Fetch von facets.js und teilt sich mit ihm über die
   * sectionRenderer-Promise-Dedup denselben Request. Das Timeout ist nur
   * Fallback für einen Fetch-Fehler.
   */
  #onFilterUpdate = () => {
    if (!this.refs.dialog.open) return;
    this.classList.add('bottom-up-sheet--filtering');
    if (this.#filteringTimeout) clearTimeout(this.#filteringTimeout);
    this.#filteringTimeout = setTimeout(() => this.#stopFiltering(), 10000);
    if (this.#isLazySection) this.refreshLazySection();
  };

  /**
   * Re-fetches the lazy section content against the CURRENT location.
   * Idempotent per URL (sectionRenderer caches); a newer call supersedes an
   * older in-flight one via the fetch token.
   */
  refreshLazySection = () => {
    if (!this.#isLazySection) return;
    this.#loadPromise = null;
    this.#ensureLoaded();
  };

  /**
   * Public prefetch hook (e.g. on:pointerenter on the trigger button) —
   * masks the first-open latency. No-op once loaded.
   */
  prefetch = () => {
    if (this.#isLazySection || this.hasAttribute('src')) this.#ensureLoaded();
  };

  #stopFiltering = () => {
    this.classList.remove('bottom-up-sheet--filtering');
    if (this.#filteringTimeout) {
      clearTimeout(this.#filteringTimeout);
      this.#filteringTimeout = null;
    }
  };

  /** @param {PopStateEvent} _event */
  #handlePopState = (_event) => {
    if (!this.refs.dialog.open) return;
    // Lazy-section sheet open during back/forward (no_history mode): the URL
    // already reverted and the facets popstate handlers morph the grid — our
    // skip-protected body must refresh itself against the reverted URL.
    if (this.#isLazySection && !this.#historyPushed) {
      this.refreshLazySection();
    }
    if (!this.#historyPushed) return;
    this.#closingFromPopState = true;
    this.closeDialog();
  };

  /**
   * In-sheet navigation. Anchors inside an opted-in container ([data-sheet-navigate])
   * reload the sheet with the link target instead of navigating the whole page.
   * Generic and markup-driven: any block whose internal links should stay in the
   * sheet just sets data-sheet-navigate — no JS change needed.
   * @param {MouseEvent} event
   */
  #handleBodyClick = (event) => {
    // Drilldown push: a [data-sheet-push] trigger inside the sheet pushes a new
    // local view instead of opening a second sheet. Handled here (on the body) so
    // it runs before the component's outside-click handler — stopPropagation keeps
    // the sheet from closing when the clicked node gets detached (cf. note below).
    const pushTrigger = event.target instanceof Element ? event.target.closest('[data-sheet-push]') : null;
    if (pushTrigger) {
      event.preventDefault();
      event.stopPropagation();
      this.#pushFromTrigger(pushTrigger);
      return;
    }
    // Only meaningful when the sheet knows how to (re)fetch content.
    if (!this.getAttribute('selector')) return;
    // Respect modifier clicks / new-tab intents and already-handled events.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const anchor = target?.closest('[data-sheet-navigate] a[href], a[href][data-sheet-navigate]');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    // Same-origin only — external links navigate normally.
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    // Stop the click from bubbling up to DialogComponent's outside-click handler
    // (#handleClick on the component). openFor() replaces the body, detaching the
    // clicked anchor; #handleClick's isClickedOutside() then sees the detached target
    // as "outside" the dialog and closes the sheet. We fully own this click, so halt
    // propagation here. (A real click runs a microtask checkpoint between listeners,
    // so merely deferring the reload would not prevent #handleClick from firing.)
    event.stopPropagation();

    this.refs.body.scrollTop = 0;
    this.openFor({ url: url.pathname + url.search });
  };

  // ===== Lazy-Fetch =====

  #ensureLoaded() {
    this.#loadPromise ??= (async () => {
      const token = ++this.#fetchToken;
      const html = await this.#fetch();
      // A newer refresh superseded this fetch — drop the stale response.
      if (token !== this.#fetchToken) return;
      await this.#inject(html);
    })().catch((error) => {
      console.error('[bottom-up-sheet] fetch failed', { src: this.getAttribute('src'), error });
      this.#loadPromise = null;
    });
    return this.#loadPromise;
  }

  async #fetch() {
    // Lazy-section mode: scoped fragment of the section at the CURRENT
    // location (read at call time — filter URLs change via pushState).
    if (this.#isLazySection) {
      const url = new URL(window.location.href);
      url.searchParams.set('_async', this.dataset.asyncScope ?? '');
      this.#loadedForUrl = location.pathname + location.search;
      return sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', true, url);
    }

    const src = this.getAttribute('src');
    if (!src) throw new Error('src attribute missing');

    const url = new URL(src, window.location.origin);
    const sectionId = url.searchParams.get('section_id');

    if (sectionId) {
      url.searchParams.delete('section_id');
      return sectionRenderer.getSectionHTML(sectionId, true, url);
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error(`fetch ${url} → ${response.status}`);
    return response.text();
  }

  async #inject(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const selector = this.getAttribute('selector');
    const node = selector
      ? doc.querySelector(selector)
      : doc.querySelector('[id^="shopify-section-"]') ?? doc.body;

    if (!node) {
      console.warn('[bottom-up-sheet] selector did not match', {
        src: this.getAttribute('src'),
        selector,
      });
      return;
    }

    // Full-page fetches (no section_id) carry their own <head>. Load missing
    // theme assets (e.g. pdp.css for a quickview opened from the homepage)
    // BEFORE the content appears → no FOUC. Section-render responses carry no
    // /assets/ links → cheap no-op.
    await syncAssetsFromDocument(doc);

    if (node.id) node.removeAttribute('id');

    // Quickview curation: blocks opted out of the quickview (show_in_quickview = false)
    // emit data-quickview-exclude. Remove them here so the sheet shows only the chosen
    // blocks while keeping the surrounding layout/structure (and the variant-fragments
    // morph controller) intact. No-op for content without such markers (filters/sort).
    for (const excluded of node.querySelectorAll('[data-quickview-exclude]')) excluded.remove();

    // Lazy-section refresh: morph into the existing content root instead of
    // replacing — preserves focus, show-more expansion and the price slider's
    // skip-protected subtree across filter updates.
    const existingRoot = this.#isLazySection && selector ? this.refs.body.querySelector(selector) : null;
    if (existingRoot && node instanceof HTMLElement) {
      morph(existingRoot, node);
      this.classList.remove('bottom-up-sheet--loading');
      this.refs.body.setAttribute('aria-busy', 'false');
      this.#stopFiltering();
      this.dispatchEvent(
        new CustomEvent('bottom-up-sheet:loaded', { bubbles: true, detail: { source: 'refresh' } })
      );
      return;
    }

    this.refs.body.replaceChildren(node);

    // Markup-driven recently-viewed tracking: fetched PDPs mark their product via
    // data-recently-viewed-id on the extracted fragment (the inline tracking module
    // in the layout never executes for fetched documents). Also fires on in-sheet
    // navigation between products.
    const tracked = node.matches?.('[data-recently-viewed-id]')
      ? node
      : node.querySelector?.('[data-recently-viewed-id]');
    const recentlyViewedId = tracked?.getAttribute('data-recently-viewed-id');
    if (recentlyViewedId) RecentlyViewed.addProduct(recentlyViewedId);

    // Sync the headline with the loaded content's primary heading (e.g. the product
    // title), so in-sheet navigation between products updates the title. Guarded on
    // an <h1> being present → facet/sort section content (no <h1>) keeps its headline.
    const heading = node.querySelector('h1')?.textContent?.trim();
    if (heading) {
      const headlineEl = this.querySelector('.bottom-up-sheet__headline');
      if (headlineEl) headlineEl.textContent = heading;
    }

    this.#reviveScripts(this.refs.body);
    this.classList.remove('bottom-up-sheet--loading');
    this.refs.body.setAttribute('aria-busy', 'false');
    this.dispatchEvent(
      new CustomEvent('bottom-up-sheet:loaded', { bubbles: true, detail: { source: 'fetch' } })
    );
  }

  #reviveScripts(root) {
    for (const oldScript of root.querySelectorAll('script')) {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
      if (oldScript.textContent) newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    }
  }

  // ===== Touch-Drag (Swipe-down to close) =====

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    if (!this.refs.dialog.open) return;
    if (event.touches.length !== 1) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('.bottom-up-sheet__handle')) {
      this.#dragOrigin = 'handle';
    } else if (target.closest('.bottom-up-sheet__header')) {
      // Aber nicht wenn der Close-Button selbst getroffen ist
      if (target.closest('.bottom-up-sheet__close')) return;
      this.#dragOrigin = 'header';
    } else if (target.closest('.bottom-up-sheet__body') && this.refs.body.scrollTop === 0) {
      // Drag wird erst beim ersten down-Move im touchmove aktiviert
      this.#dragOrigin = 'body-top';
    } else {
      return;
    }

    this.#dragStartY = event.touches[0].clientY;
    this.#dragStartTime = performance.now();
    this.#dragDelta = 0;
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    if (this.#dragOrigin === null || this.#dragStartY === null) return;
    if (event.touches.length !== 1) return;

    const currentY = event.touches[0].clientY;
    const delta = currentY - this.#dragStartY;

    if (delta <= 0) {
      // User zieht hoch — bei body-top den Drag-Modus releasen, Body übernimmt
      if (this.#dragOrigin === 'body-top') {
        this.refs.dialog.style.transform = '';
        this.#resetDragState();
      }
      return;
    }

    // Bei body-top: nur tracken solange Body wirklich scrollTop=0 ist
    if (this.#dragOrigin === 'body-top' && this.refs.body.scrollTop > 0) {
      this.refs.dialog.style.transform = '';
      this.#resetDragState();
      return;
    }

    this.#dragDelta = delta;
    this.refs.dialog.style.transform = `translateY(${delta}px)`;
    if (event.cancelable) event.preventDefault();
  };

  /** @param {TouchEvent} _event */
  #handleTouchEnd = (_event) => {
    if (this.#dragOrigin === null) return;

    const dialog = this.refs.dialog;
    const sheetHeight = dialog.getBoundingClientRect().height;
    const threshold = Math.min(100, sheetHeight * 0.25);
    const duration = performance.now() - this.#dragStartTime;
    const velocity = duration > 0 ? this.#dragDelta / duration : 0;

    const shouldClose = this.#dragDelta > threshold || velocity > 0.5;

    if (shouldClose) {
      // Transform behalten — Slide-Down-Animation läuft von hier aus weiter.
      // #onDialogClose räumt das Transform/Transition danach auf.
      this.closeDialog();
    } else {
      // Snap zurück
      dialog.style.transition = 'transform 200ms ease-out';
      dialog.style.transform = 'translateY(0)';
      const cleanup = () => {
        dialog.style.transition = '';
        dialog.style.transform = '';
        dialog.removeEventListener('transitionend', cleanup);
      };
      dialog.addEventListener('transitionend', cleanup);
    }
    this.#resetDragState();
  };

  #resetDragState() {
    this.#dragStartY = null;
    this.#dragOrigin = null;
    this.#dragDelta = 0;
    this.#dragStartTime = 0;
  }
}

/**
 * Resolve the content a [data-sheet-push] trigger points to: a hidden
 * <template data-usp-detail="<handle>"> rendered elsewhere on the page, cloned.
 * @param {Element} trigger
 * @returns {DocumentFragment | null}
 */
function resolveSheetPushContent(trigger) {
  const handle = trigger.getAttribute('data-sheet-push');
  if (!handle) return null;
  const tpl = document.querySelector(`template[data-usp-detail="${CSS.escape(handle)}"]`);
  return tpl instanceof HTMLTemplateElement ? /** @type {DocumentFragment} */ (tpl.content.cloneNode(true)) : null;
}

// Out-of-sheet [data-sheet-push] triggers (e.g. on a normal PDP, sheet closed) open
// the target sheet with the content as its first view. In-sheet triggers are handled
// by the sheet's own body click handler (push, no reopen) and skipped here.
let sheetPushBound = false;
function bindSheetPushTriggers() {
  if (sheetPushBound) return;
  sheetPushBound = true;
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-sheet-push]') : null;
    if (!trigger || trigger.closest('bottom-up-sheet')) return;
    const content = resolveSheetPushContent(trigger);
    if (!content) return;
    event.preventDefault();
    const targetId = trigger.getAttribute('data-sheet-target') || 'product-quickview-sheet';
    const sheet = document.getElementById(targetId);
    if (sheet && typeof sheet.openWithContent === 'function') {
      sheet.openWithContent({ content, headline: trigger.getAttribute('data-headline') ?? '' });
    }
  });
}
bindSheetPushTriggers();

if (!customElements.get('bottom-up-sheet')) {
  customElements.define('bottom-up-sheet', BottomUpSheetComponent);
}
